'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { toast } from '@/components/ui/use-toast';

type UseEntityDeleteOptions<TArgs> = {
  // The mutation function (e.g. `mutateAsync` from a useMutation hook).
  mutate: (args: TArgs) => Promise<unknown>;
  // The argument to pass to the mutation. Resolved lazily on `run()` so callers
  // can close over fresh state (id, spaceId, ...) without re-creating the hook.
  buildArgs: () => TArgs;
  // Translation key for the success toast, or `null` to skip the toast on
  // success (used by autocourse where the row's disappearance is the signal).
  successMessageKey: string | null;
  // Translation key for the error toast.
  errorMessageKey: string;
  // When `true`, the error toast includes `error.message` as its description
  // (used by autocourse — surfaces the backend reason). Defaults to `false`
  // to match the other entities' silent toasts.
  includeErrorDetail?: boolean;
  // Fires after a successful mutation, before the success toast. Typically
  // refetches the list and/or closes the confirmation modal.
  onSuccess?: () => void;
};

type UseEntityDeleteResult = {
  run: () => Promise<void>;
};

/**
 * Wraps a delete mutation in the shared try/catch/toast envelope used by every
 * entity action cell (course, sequence, quiz, glossary, certificate, ...).
 *
 * The previous per-screen `handleDelete` callbacks differed only in mutation,
 * args, and toast copy — this hook captures that envelope once.
 */
function useEntityDelete<TArgs>({
  mutate,
  buildArgs,
  successMessageKey,
  errorMessageKey,
  includeErrorDetail = false,
  onSuccess,
}: UseEntityDeleteOptions<TArgs>): UseEntityDeleteResult {
  const translate = useTranslations();

  const run = useCallback(async () => {
    try {
      await mutate(buildArgs());
      if (onSuccess !== undefined) {
        onSuccess();
      }
      if (typeof successMessageKey === 'string') {
        toast({ title: translate(successMessageKey) });
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: translate(errorMessageKey),
        ...(includeErrorDetail === true && {
          description: error instanceof Error ? error.message : '',
        }),
      });
    }
  }, [
    mutate,
    buildArgs,
    onSuccess,
    successMessageKey,
    errorMessageKey,
    includeErrorDetail,
    translate,
  ]);

  return { run };
}

export default useEntityDelete;
